import type { PilotBoard } from "../pilotBoard";
import type { LogDraft, SimTimes } from "../logDraft";
import { FLIGHT_TABS, FLIGHT_TAB_LABEL, type FlightTab } from "../shell/tabs";
import { DispatchInbox } from "./DispatchInbox";
import { EmptyState } from "./EmptyState";
import { LogForm } from "./LogForm";

const NO_ASSIGNMENT = "The desk has not assigned a flight. This screen fills in when dispatch assigns you.";

function FlightTabIcon({ tab }: { tab: FlightTab }) {
  const common = {
    viewBox: "0 0 24 24",
    width: 22,
    height: 22,
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.75,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  if (tab === "brief") {
    return (
      <svg {...common}>
        <path d="M6 4.5h9l3 3V19.5H6z" />
        <path d="M15 4.5V8h3.5M8.5 12h7M8.5 15.5h5" />
      </svg>
    );
  }
  if (tab === "duty") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="7.5" />
        <path d="M12 8.5V12l2.5 2" />
      </svg>
    );
  }
  if (tab === "log") {
    return (
      <svg {...common}>
        <path d="M7 4.5h10v15H7z" />
        <path d="M9.5 8.5h5M9.5 12h5M9.5 15.5h3" />
      </svg>
    );
  }
  if (tab === "pax") {
    return (
      <svg {...common}>
        <circle cx="9" cy="9" r="2.2" />
        <circle cx="15.5" cy="9.5" r="1.8" />
        <path d="M4.5 17.5c.6-2.2 2.4-3.3 4.5-3.3s3.9 1.1 4.5 3.3M13 14.4c1.2-.5 2.4-.4 3.6.2 1.1.6 1.8 1.6 2.2 2.9" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M8 6.5h10v12H8z" />
      <path d="M6 9.5h2M6 13h2M10.5 10.5l1.2 1.2 2.3-2.4" />
    </svg>
  );
}

/** Phone flight sections. Five equal icons along the bottom, not a scrolling strip. */
export function FlightTabs({ tab, onTab }: { tab: FlightTab; onTab: (next: FlightTab) => void }) {
  return (
    <nav className="flight-tabs" role="tablist" aria-label="Flight" data-testid="flight-tabs">
      {FLIGHT_TABS.map((id) => (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={id === tab}
          aria-label={FLIGHT_TAB_LABEL[id]}
          className={id === tab ? "flight-tab is-active" : "flight-tab"}
          data-testid={`flight-tab-${id}`}
          onClick={() => onTab(id)}
        >
          <FlightTabIcon tab={id} />
          <span className="flight-tab-label">{FLIGHT_TAB_LABEL[id]}</span>
        </button>
      ))}
    </nav>
  );
}

function Lines({ rows, empty }: { rows: string[]; empty: string }) {
  if (!rows.length) return empty ? <p className="muted">{empty}</p> : null;
  return (
    <ul className="pilot-list">
      {rows.map((row, index) => (
        <li key={`${index}-${row}`}>{row}</li>
      ))}
    </ul>
  );
}

function BriefPane({
  board,
  unread,
  onMarkRead,
  onReply,
}: {
  board: PilotBoard;
  unread: number;
  onMarkRead?: () => void;
  onReply?: (packet: string) => Promise<{ ok: true } | { ok: false; error: string }> | { ok: true } | { ok: false; error: string };
}) {
  return (
    <div data-testid="pilot-brief">
      <article className="panel">
        <p className="kicker">Route</p>
        <h1 className="trip-route">{board.route || "No route"}</h1>
        <p className="muted">
          {board.callsign}
          {board.tail ? ` · ${board.tail}` : ""}
        </p>
        <p>{board.std}</p>
      </article>
      {board.handover ? (
        <article className="panel" data-testid="handover-card">
          <h2>Handover</h2>
          {board.handover.fob ? <p>{board.handover.fob}</p> : null}
          {board.handover.note ? <p className="muted">{board.handover.note}</p> : null}
        </article>
      ) : null}
      <article className="panel">
        <h2>Fuel plan</h2>
        <Lines rows={board.fuel} empty="No fuel plan on this ticket." />
      </article>
      <article className="panel">
        <h2>Weights</h2>
        <Lines rows={board.wb} empty="W&B sheet is not on this board." />
      </article>
      <article className="panel">
        <h2>Weather</h2>
        <Lines rows={[board.originMetar, board.destMetar].filter(Boolean)} empty="No METAR on this ticket." />
      </article>
      <article className="panel">
        <h2>NOTAMs</h2>
        <Lines rows={board.notams.map((row) => row.text)} empty="No flight-affecting NOTAMs on this board." />
      </article>
      <article className="panel">
        <h2>Open squawks</h2>
        <Lines rows={board.squawks.map((row) => row.text)} empty="No open squawks for this tail." />
      </article>
      <article className="panel">
        <h2>SimBrief OFP</h2>
        <Lines rows={board.ofp} empty="No SimBrief OFP on this ticket." />
      </article>
      <DispatchInbox board={board} unread={unread} onMarkRead={onMarkRead} onReply={onReply} />
    </div>
  );
}

function DutyPane({
  board,
  onClock,
  clockNote,
}: {
  board: PilotBoard;
  onClock: (action: "in" | "out") => void;
  clockNote: string;
}) {
  const duty = board.duty;
  return (
    <article className="panel" data-testid="pilot-duty">
      <p className="kicker">Duty</p>
      {duty ? (
        <>
          <h1>
            <span className={`legality-dot is-${duty.level}`} data-testid="duty-dot" /> {duty.statusLabel}
          </h1>
          <ul className="pilot-list" data-testid="legality-list">
            <li>FDP {duty.fdp} / limit {duty.fdpLimit}</li>
            <li>Flight {duty.flight} / limit {duty.flightLimit}</li>
            <li>Rest {duty.rest} / minimum {duty.restLimit}</li>
          </ul>
        </>
      ) : (
        <p className="muted">No duty clock for this crew.</p>
      )}
      <div className="row">
        <button type="button" className="btn" data-testid="clock-in" onClick={() => onClock("in")} disabled={Boolean(duty?.onDuty)}>
          Clock in
        </button>
        <button type="button" className="btn ghost" data-testid="clock-out" onClick={() => onClock("out")} disabled={!duty?.onDuty}>
          Clock out
        </button>
      </div>
      {clockNote ? <p className="muted">{clockNote}</p> : null}
      {board.canDispatchWrite ? null : (
        <p className="field-hint">A pilot clock stays on this phone. Posting the company duty clock needs a dispatcher account.</p>
      )}
    </article>
  );
}

function PaxPane({ board }: { board: PilotBoard }) {
  return (
    <article className="panel" data-testid="pilot-pax">
      <h2>Passengers</h2>
      <p>{board.pax ? `${board.pax} pax` : "No passengers on this ticket."}</p>
      <Lines rows={board.paxNames} empty="" />
      <h2>Catering</h2>
      <Lines rows={board.catering} empty="No catering line on this ticket." />
    </article>
  );
}

function ChecksPane({ board }: { board: PilotBoard }) {
  return (
    <article className="panel" data-testid="pilot-checks">
      <h2>Checklist</h2>
      {board.checks.length ? (
        <ul className="pilot-list">
          {board.checks.map((row) => (
            <li key={row.id} className={`pilot-check is-${row.state}`}>
              <strong>{row.label}</strong>
              {row.detail ? <span className="muted"> {row.detail}</span> : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">No checklist on this ticket.</p>
      )}
    </article>
  );
}

export function FlightDetail({
  board,
  tab,
  draft,
  onDraft,
  onClock,
  clockNote,
  unread = 0,
  onMarkRead,
  onReply,
}: {
  board: PilotBoard;
  tab: FlightTab;
  draft: LogDraft | null;
  onDraft: (next: LogDraft) => void;
  onClock: (action: "in" | "out") => void;
  clockNote: string;
  unread?: number;
  onMarkRead?: () => void;
  onReply?: (packet: string) => Promise<{ ok: true } | { ok: false; error: string }> | { ok: true } | { ok: false; error: string };
}) {
  if (!board.tripId && tab !== "duty") {
    return (
      <div data-testid="flight-detail" data-flight-tab={tab}>
        <EmptyState title="No assignment" body={NO_ASSIGNMENT} />
      </div>
    );
  }
  const brief = <BriefPane board={board} unread={unread} onMarkRead={onMarkRead} onReply={onReply} />;
  const log = draft ? <LogForm draft={draft} sim={board.simTimes as SimTimes} onChange={onDraft} /> : null;
  const split = tab === "brief" || tab === "log";
  return (
    <div data-testid="flight-detail" data-flight-tab={tab}>
      {!board.tripId ? <EmptyState title="No assignment" body={NO_ASSIGNMENT} /> : null}
      {split ? (
        <div className="flight-split">
          <div className={tab === "log" ? "flight-pane is-hidden-phone" : "flight-pane"}>{brief}</div>
          <div className={tab === "brief" ? "flight-pane is-hidden-phone" : "flight-pane"}>{log}</div>
        </div>
      ) : null}
      {tab === "duty" ? <DutyPane board={board} onClock={onClock} clockNote={clockNote} /> : null}
      {tab === "pax" ? <PaxPane board={board} /> : null}
      {tab === "checks" ? <ChecksPane board={board} /> : null}
    </div>
  );
}
